// ─────────────────────────────────────────────
// KLOTHIQ — useCustomers hook
// ─────────────────────────────────────────────

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import type { Database } from '@klothiq/domain';

type Customer = Database['public']['Tables']['customers']['Row'];
type CustomerInsert = Database['public']['Tables']['customers']['Insert'];

export const CUSTOMERS_KEY = 'customers';

// Optional contact fields use SQL NULL when cleared; omitted patch fields stay omitted.
function normalizeCustomerFields<T extends { phone?: string | null; pincode?: string | null }>(
  values: T,
) {
  return {
    ...values,
    ...(Object.hasOwn(values, 'phone') ? { phone: values.phone?.trim() || null } : {}),
    ...(Object.hasOwn(values, 'pincode') ? { pincode: values.pincode?.trim() || null } : {}),
  };
}

export function useCustomers(search?: string) {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [CUSTOMERS_KEY, orgId, search],
    queryFn: async (): Promise<Customer[]> => {
      let query = supabase
        .from('customers')
        .select('*')
        .eq('organization_id', orgId!)
        .eq('is_active', true)
        .order('name');

      if (search?.trim()) {
        query = query.or(`name.ilike.%${search}%,phone.ilike.%${search}%,city.ilike.%${search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!orgId,
    staleTime: 1000 * 60,
  });
}

export function useCustomer(customerId?: string) {
  const { organization } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [CUSTOMERS_KEY, orgId, customerId],
    queryFn: async (): Promise<Customer | null> => {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('id', customerId!)
        .eq('organization_id', orgId!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!orgId && !!customerId,
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    // Routed through create_customer_with_audit (FEAT-SALE-ENH-001) so quick-add
    // customer creation writes an audit_logs entry — the plain insert() this used
    // to call had no audit trail at all, unlike every other mutation in this app.
    mutationFn: async (
      values: Omit<CustomerInsert, 'organization_id' | 'created_by' | 'updated_by'>,
    ) => {
      const { data, error } = await supabase.rpc('create_customer_with_audit', {
        p_data: normalizeCustomerFields(values),
      });
      if (error) throw error;
      return data as Customer;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CUSTOMERS_KEY] }),
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async ({ id, ...values }: Partial<Customer> & { id: string }) => {
      const { data, error } = await supabase
        .from('customers')
        .update({
          ...normalizeCustomerFields(values),
          updated_by: profile?.id,
        })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CUSTOMERS_KEY] }),
  });
}
